import {
    combineReducers,
    configureStore,
    createImmutableStateInvariantMiddleware,
    createSerializableStateInvariantMiddleware,
    nanoid,
    Unsubscribe,
} from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-extraneous-dependencies
import logger from 'redux-logger';
import thunk from 'redux-thunk';

import { ENV } from 'src/config/environments';

import { address } from './address';
import { app } from './app';
import { expedient } from './expedient';
import { push } from './push';
import { tramit } from './tramit';
import { user } from './user';

const coreReducers = {
    app,
    push,
    user,
    expedient,
    tramit,
    address,
};

const store = configureStore({
    reducer: combineReducers(coreReducers),
    middleware: (ENV.env === 'production' ? [thunk] : [
        thunk,
        createImmutableStateInvariantMiddleware(),
        createSerializableStateInvariantMiddleware(),
        ENV.isReduxLoggerEnabled ? logger : (() => () => next => action => next(action))(),
    ]),
});

export default store;
export { coreReducers };

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// We will save the unsubscribe function in every component, under this property
const unsubscribePropName = `unsusbscribe_${nanoid()}`;

export function mapDispatchToProps<C extends { [K in keyof P]: any }, P>(component: C, props: P) {
    Object.keys(props).forEach((actionName) => {
        const action = props[actionName];
        Object.defineProperty(component, actionName, {
            get: () => (...args: any[]) => store.dispatch(action(...args)),
            configurable: true,
            enumerable: true,
        });
    });
}

function doMapStateToProps(component: any, mapState: any) {
    const mergeProps = mapState(store.getState());
    Object.keys(mergeProps).forEach((newPropName) => {
        const newPropValue = mergeProps[newPropName];
        // eslint-disable-next-line no-param-reassign
        component[newPropName] = newPropValue;
    });
}

export function mapStateToProps<C extends R, R, S extends RootState>(component: C, mapState: (state: S) => R): Unsubscribe {
    // Define unsubscribePropName property to object, that will keep the unsubscribe for the component
    let unsubscribe = component[unsubscribePropName];
    if (!unsubscribe) {
        // First call of mapStateToProps for this component
        unsubscribe = store.subscribe(() => {
            doMapStateToProps(component, mapState);
        });
        Object.defineProperty(component, unsubscribePropName, {
            get: () => unsubscribe,
            configurable: false,
            enumerable: false,
        });

        // eslint-disable-next-line dot-notation, @typescript-eslint/dot-notation
        const disconnectedCallback = component['disconnectedCallback'] || (() => { });
        Object.defineProperty(component, 'disconnectedCallback', {
            get: () => () => {
                const unsubscr = component[unsubscribePropName];
                unsubscr();
                return disconnectedCallback.bind(component)();
            },
            configurable: true,
            enumerable: true,
        });
    }
    doMapStateToProps(component, mapState);
    // Return a function that when called will call unsubscribe from component
    return () => {
        const unsubscr = component[unsubscribePropName];
        return unsubscr();
    };
}

// Action exports
export * from './app';
export * from './push';
export * from './expedient';
export * from './tramit';
export * from './user';
// Thunk action exports
export * from './thunkActions';
// Types exports
export * from './types';
